import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { VideoSoundScope } from '@/components/documents/VideoSoundQuestion'
import {
  captureRefusal,
  countSelectedCaptures,
  extractableIds,
} from '@/features/documents/capture-integrity'
import { inFlightRefusal } from '@/features/documents/delete-refusal'
import {
  DOCUMENT_PROGRESS_POLL_MS,
  followedDocumentIds,
  holdProgressAgainst,
} from '@/features/documents/document-progress'
import { NEWEST_UPLOAD_FIRST, nextSort } from '@/features/documents/document-sort'
import { toExtractResults } from '@/features/documents/extract-results'
import { extractionInProgress } from '@/features/documents/extraction-progress'
import { useExtractionRuns } from '@/features/documents/hooks/useExtractionRuns'
import {
  useVideoSoundQuestion,
  type VideoSoundQuestion,
} from '@/features/documents/hooks/useVideoSoundQuestion'
import { applyTableProcessing, tableProcessingUpdate } from '@/features/documents/table-processing'
import { videoAnalysisRefusal } from '@/features/documents/video-refusal'
import { videoSoundScope } from '@/features/documents/video-sound'
import { useCaseSchemaStatus } from '@/features/schema/hooks/useCaseSchemaStatus'
import { caseKeys } from '@/hooks/use-case-queries'
import { useContentSearch } from '@/hooks/use-content-search'
import { useExtensionEnabled, VIDEO_PROCESSING_EXTENSION_ID } from '@/hooks/use-extensions'
import { api } from '@/services/api'
import type {
  CaseDocumentSort,
  CaseDocumentStatusFilter,
  CaseStatusCounts,
  ExtractionRun,
  TabularExtractionMode,
} from '@/services/api/cases'
import { errorMessage } from '@/utils/errors'
import { isVideoFileType } from '@/utils/video-file'
import {
  AUTO_PIPELINE_ID,
  type ExtractResults,
  type FolderTreeFile,
  type FolderTreeFolder,
  type FolderTreeResponse,
  type Phase2Workflow,
  STATUS_OPTIONS,
} from '../types'

const PAGE_SIZE = 100

/** How often the runs are read again while one is going: the card shows the step that runs. */
const RUNS_POLL_MS = 2000

export interface UseDocumentListArgs {
  caseId: string
  onPathChange?: (path: string | undefined) => void
  refreshKey?: number
}

interface OrderedFolderItem {
  type: 'folder'
  key: string
  folder: FolderTreeFolder
}
interface OrderedFileItem {
  type: 'file'
  key: string
  file: FolderTreeFile
}
export type OrderedItem = OrderedFolderItem | OrderedFileItem

interface DeleteConfirm {
  message: string
  onConfirm: () => Promise<void>
}

interface SelectedDocumentMeta {
  id: string
  file_type: string | null
  file_size: number
  tabular_extraction_mode?: TabularExtractionMode
}

export interface RunExtractionOptions {
  /** Selected documents to leave out of this run (tabular files handled by structured ingest). */
  excludeDocumentIds?: string[]
  /** The answer to "process the sound of the videos too?" when the run asked it (PRD-0115). */
  videoSound?: boolean
}

export interface UseDocumentListResult {
  schema: ReturnType<typeof useCaseSchemaStatus>
  // refs
  scrollRef: React.RefObject<HTMLDivElement | null>
  // tree / loading
  loading: boolean
  loadingMore: boolean
  treeData: FolderTreeResponse | null
  // derived collections
  orderedItems: OrderedItem[]
  filteredFolders: FolderTreeFolder[]
  filteredFiles: FolderTreeFile[]
  displayedItems: number
  totalItems: number
  statusCounts: Record<string, number>
  activeStatus: CaseDocumentStatusFilter | undefined
  // order: by upload or processing time; the server pages in it
  sort: CaseDocumentSort
  toggleSort: (by: CaseDocumentSort['by']) => void
  // search
  searchQuery: string
  setSearchQuery: (q: string) => void
  // status filter
  statusFilters: Set<string>
  clearStatusFilter: () => void
  toggleStatusFilter: (value: string) => void
  // navigation
  currentPath: string[]
  breadcrumbItems: string[]
  navigateInto: (folderName: string) => void
  navigateTo: (index: number) => void
  // selection
  selectedIds: Set<string>
  selectedFolderPaths: Set<string>
  hasSelection: boolean
  selectAllChecked: boolean
  selectAllIndeterminate: boolean
  selectedTotalItems: number
  /** Selected HUMINT captures: immutable, so they block run, reset and delete (ADR-0045). */
  selectedCaptureCount: number
  handleItemToggle: (index: number) => void
  handleSelectAll: () => void
  handleClearSelection: () => void
  // pipelines
  pipelines: Phase2Workflow[]
  selectedPipelineId: string | null
  handlePipelineSelect: (id: string) => void
  selectedDocumentMetas: SelectedDocumentMeta[]
  // extraction
  isRetrying: boolean
  autoRunning: boolean
  extractResults: ExtractResults | null
  showExtractionModal: boolean
  setShowExtractionModal: (open: boolean) => void
  handleRetryPipeline: () => void
  handleRunExtraction: (
    schemaName?: string,
    userPrompt?: string,
    schemaVersion?: number,
    options?: RunExtractionOptions
  ) => Promise<void>
  runExtractionOnPending: (statuses?: string[]) => Promise<void>
  /**
   * The extraction in progress — the one running run, or the sum of every run still going — or
   * undefined when none is.
   */
  runInProgress: ExtractionRun | undefined
  pendingCount: number
  failedCount: number
  // delete
  deleteConfirm: DeleteConfirm | null
  setDeleteConfirm: (next: DeleteConfirm | null) => void
  handleDeleteDocument: (documentId: string) => void
  handleDeleteFolder: (folderPath: string, fileCount: number) => void
  handleBulkDelete: () => void
  // reset extraction
  handleResetExtraction: (documentIds: string[]) => Promise<void>
  handleResetSingleExtraction: (docId: string) => void
  // video analysis (PRD-0074): queue one recording on the video lane
  /** Whether the Video Processing extension is on; off, video rows say why. */
  videoAvailable: boolean
  handleAnalyseVideo: (docId: string) => void
  /**
   * Whether the selection's run asks about the sound of its videos (PRD-0115), and about what: null
   * for a selection with no video and no folder.
   */
  selectionVideoSound: VideoSoundScope | null
  /** The question a run without a dialog of its own asks before it starts (Run pending, Analyse video). */
  videoSoundQuestion: VideoSoundQuestion
}

/**
 * Container data + actions for the case document list. Owns ALL imperative / IO
 * concerns — paginated folder-tree loading, infinite scroll, polling, search,
 * selection, bulk delete, extraction (auto-run + modal), the extraction-runs
 * query, and queryClient invalidations — so `DocumentList` can stay a thin
 * presentational orchestrator.
 */
export function useDocumentList({
  caseId,
  onPathChange,
  refreshKey,
}: UseDocumentListArgs): UseDocumentListResult {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const schema = useCaseSchemaStatus(caseId)
  const videoAvailable = useExtensionEnabled(VIDEO_PROCESSING_EXTENSION_ID).enabled

  const [treeData, setTreeData] = useState<FolderTreeResponse | null>(null)
  useEffect(
    () =>
      queryClient.getQueryCache().subscribe(event => {
        if (event.type !== 'updated' || event.action.type !== 'success') return
        const update = tableProcessingUpdate(caseId, event.query.queryKey, event.query.state.data)
        if (update) setTreeData(tree => applyTableProcessing(tree, update))
      }),
    [caseId, queryClient]
  )
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [totalFiles, setTotalFiles] = useState(0)
  const [fileOffset, setFileOffset] = useState(0)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const { data: contentSearchData } = useContentSearch(caseId, searchQuery)
  const matchedDocIds = useMemo(() => {
    const ids = new Set<string>()
    if (!contentSearchData) return ids
    for (const e of contentSearchData.entities) {
      if (e.document_id) ids.add(e.document_id)
    }
    for (const c of contentSearchData.chunks) {
      if (c.document_id) ids.add(c.document_id)
    }
    for (const d of contentSearchData.documents) {
      ids.add(d.document_id)
    }
    return ids
  }, [contentSearchData])
  const [statusFilters, setStatusFilters] = useState<Set<string>>(new Set())
  const activeStatus = useMemo<CaseDocumentStatusFilter | undefined>(() => {
    const first = statusFilters.values().next().value
    return (first ?? undefined) as CaseDocumentStatusFilter | undefined
  }, [statusFilters])
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [selectedFolderPaths, setSelectedFolderPaths] = useState<Set<string>>(new Set())
  const [isRetrying, setIsRetrying] = useState(false)
  const [extractResults, setExtractResults] = useState<ExtractResults | null>(null)
  const [pipelines, setPipelines] = useState<Phase2Workflow[]>([])
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null)
  const [showExtractionModal, setShowExtractionModal] = useState(false)
  const [currentPath, setCurrentPath] = useState<string[]>([])
  const [sort, setSort] = useState<CaseDocumentSort>(NEWEST_UPLOAD_FIRST)
  const toggleSort = useCallback(
    (by: CaseDocumentSort['by']) => setSort(current => nextSort(current, by)),
    []
  )
  // The fetches read the order through a ref, so a new order hands the rows no new callbacks; the
  // fetch effect below asks again when it changes (this effect runs before that one).
  const sortRef = useRef(sort)
  useEffect(() => {
    sortRef.current = sort
  }, [sort])
  const isShiftHeld = useRef(false)
  const lastSelectedIndex = useRef<number | null>(null)

  const fetchTree = useCallback(
    async (path?: string, silent = false, status?: CaseDocumentStatusFilter) => {
      if (!silent) setLoading(true)
      setFileOffset(0)
      try {
        const data = await api.cases.getCaseFolderTree(caseId, {
          path,
          limit: PAGE_SIZE,
          offset: 0,
          status,
          sort: sortRef.current,
        })
        setTreeData(data)
        setHasMore(data.has_more ?? false)
        setTotalFiles(data.total_files ?? data.files.length)
        setFileOffset(data.files.length)
      } catch (err: unknown) {
        console.error('Failed to fetch folder tree:', err)
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [caseId]
  )

  const fetchMoreFiles = useCallback(async () => {
    if (loadingMore || !hasMore) return
    setLoadingMore(true)
    try {
      const path = currentPath.length > 0 ? currentPath.join('/') : undefined
      const data = await api.cases.getCaseFolderTree(caseId, {
        path,
        limit: PAGE_SIZE,
        offset: fileOffset,
        status: activeStatus,
        sort: sortRef.current,
      })
      setTreeData(prev => {
        if (!prev) return data
        const existingIds = new Set(prev.files.map(f => f.document.id))
        const newFiles = data.files.filter(f => !existingIds.has(f.document.id))
        return {
          ...prev,
          files: [...prev.files, ...newFiles],
          has_more: data.has_more,
          total_files: data.total_files,
        }
      })
      setHasMore(data.has_more ?? false)
      setFileOffset(prev => prev + data.files.length)
    } catch (err: unknown) {
      console.error('Failed to fetch more files:', err)
    } finally {
      setLoadingMore(false)
    }
  }, [caseId, currentPath, fileOffset, hasMore, loadingMore, activeStatus])

  // Re-fetch tree whenever path, refresh, active status filter or sort changes.
  // refreshKey and sort are intentional triggers: refreshKey forces a refetch even
  // when nothing else in the dependency list changed, and fetchTree reads the sort
  // through its ref.
  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshKey and sort are deliberate refetch triggers
  useEffect(() => {
    const path = currentPath.length > 0 ? currentPath.join('/') : undefined
    void fetchTree(path, false, activeStatus)
    onPathChange?.(path)
  }, [fetchTree, currentPath, onPathChange, refreshKey, activeStatus, sort])

  // The rows that are on their way are followed: any document that is being processed, and an
  // audio file from the queue on (it is processed on its own from the moment it is uploaded).
  // They are asked for by id — their status and stage — and never by reading the tree again,
  // which loads every document of the case: a new stage is put on the row that is shown, and
  // the tree is read again only once a status changed. A read that fails is no news: the rows
  // are asked for again on the next turn. Nothing is asked while the page is not looked at.
  const shownTree = useRef(treeData)
  useEffect(() => {
    shownTree.current = treeData
  }, [treeData])
  const followedKey = useMemo(
    () => followedDocumentIds(treeData?.files ?? []).join(','),
    [treeData]
  )
  useEffect(() => {
    if (followedKey === '') return
    const askedIds = followedKey.split(',')
    const path = currentPath.length > 0 ? currentPath.join('/') : undefined
    let stopped = false
    let asking = false
    const ask = async () => {
      if (asking || document.hidden) return
      asking = true
      try {
        const progress = await api.cases.getDocumentProgress(caseId, askedIds)
        const tree = shownTree.current
        if (stopped || !tree) return
        const held = holdProgressAgainst(tree, askedIds, progress)
        if (held.reload) void fetchTree(path, true, activeStatus)
        else setTreeData(prev => (prev ? holdProgressAgainst(prev, askedIds, progress).tree : prev))
      } catch (err: unknown) {
        console.error('Failed to read document progress:', err)
      } finally {
        asking = false
      }
    }
    const id = setInterval(() => void ask(), DOCUMENT_PROGRESS_POLL_MS)
    return () => {
      stopped = true
      clearInterval(id)
    }
  }, [followedKey, caseId, fetchTree, currentPath, activeStatus])

  // Infinite scroll: load more when near bottom
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const handleScroll = () => {
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
        void fetchMoreFiles()
      }
    }
    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => el.removeEventListener('scroll', handleScroll)
  }, [fetchMoreFiles])

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      isShiftHeld.current = e.shiftKey
      if (e.key === 'Escape') {
        setSelectedIds(new Set())
        setSelectedFolderPaths(new Set())
        lastSelectedIndex.current = null
      }
    }
    const up = (e: KeyboardEvent) => {
      isShiftHeld.current = e.shiftKey
    }
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
    }
  }, [])

  useEffect(() => {
    api.cases
      .getPhase2Workflows()
      .then(items => {
        setPipelines(items)
        // Default to "Auto" — backend dispatches per-document via PipelineDispatchService.
        setSelectedPipelineId(AUTO_PIPELINE_ID)
      })
      .catch((err: unknown) => console.error('Failed to fetch pipelines:', err))
  }, [])

  const statusCounts = useMemo<Record<string, number>>(() => {
    const server = treeData?.status_counts as CaseStatusCounts | undefined
    if (server) return server
    const zero: Record<string, number> = { all: 0 }
    for (const opt of STATUS_OPTIONS) zero[opt.value] = 0
    return zero
  }, [treeData])

  const filteredFolders = useMemo(() => {
    return treeData?.folders ?? []
  }, [treeData])

  const filteredFiles = useMemo(() => {
    // Status filtering is server-side; here we only narrow by search query.
    let files = treeData?.files ?? []
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      files = files.filter(
        f => f.name.toLowerCase().includes(q) || matchedDocIds.has(f.document.id)
      )
    }
    return files
  }, [treeData, searchQuery, matchedDocIds])

  const orderedItems = useMemo<OrderedItem[]>(
    () => [
      ...filteredFolders.map(f => ({ type: 'folder' as const, key: f.path, folder: f })),
      ...filteredFiles.map(f => ({ type: 'file' as const, key: f.document.id, file: f })),
    ],
    [filteredFolders, filteredFiles]
  )

  const displayedItems = filteredFolders.length + filteredFiles.length
  const totalItems = filteredFolders.length + totalFiles

  const navigateInto = useCallback((folderName: string) => {
    setCurrentPath(prev => [...prev, folderName])
    setTreeData(null)
    setSelectedIds(new Set())
    setSelectedFolderPaths(new Set())
    lastSelectedIndex.current = null
  }, [])

  const navigateTo = useCallback((index: number) => {
    setCurrentPath(prev => prev.slice(0, index))
    setTreeData(null)
    setSelectedIds(new Set())
    setSelectedFolderPaths(new Set())
    lastSelectedIndex.current = null
  }, [])

  // Auto-select Auto pipeline when selection starts (backend dispatches per-doc)
  const hasSelection = selectedIds.size > 0 || selectedFolderPaths.size > 0
  useEffect(() => {
    if (hasSelection && !selectedPipelineId) {
      setSelectedPipelineId(AUTO_PIPELINE_ID)
    }
  }, [hasSelection, selectedPipelineId])

  // Resolve selected document metadata (file_type + file_size) for the
  // estimate panel in the Run Extraction modal.
  const selectedDocumentMetas = useMemo<SelectedDocumentMeta[]>(() => {
    const files = treeData?.files ?? []
    return files
      .filter(f => selectedIds.has(f.document.id))
      .map(f => ({
        id: f.document.id,
        file_type: f.document.file_type,
        file_size: f.document.file_size,
        tabular_extraction_mode: f.document.tabular_extraction_mode,
      }))
  }, [treeData, selectedIds])

  const handleItemToggle = useCallback(
    (index: number) => {
      if (isShiftHeld.current && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index)
        const end = Math.max(lastSelectedIndex.current, index)
        const newFileIds = new Set<string>()
        const newFolderPaths = new Set<string>()
        for (let i = start; i <= end; i++) {
          const item = orderedItems[i]
          if (item.type === 'file') newFileIds.add(item.key)
          else newFolderPaths.add(item.key)
        }
        setSelectedIds(prev => {
          const next = new Set(prev)
          newFileIds.forEach(id => next.add(id))
          return next
        })
        setSelectedFolderPaths(prev => {
          const next = new Set(prev)
          newFolderPaths.forEach(p => next.add(p))
          return next
        })
      } else {
        const item = orderedItems[index]
        if (item.type === 'file') {
          setSelectedIds(prev => {
            const next = new Set(prev)
            if (next.has(item.key)) next.delete(item.key)
            else next.add(item.key)
            return next
          })
        } else {
          setSelectedFolderPaths(prev => {
            const next = new Set(prev)
            if (next.has(item.key)) next.delete(item.key)
            else next.add(item.key)
            return next
          })
        }
      }
      lastSelectedIndex.current = index
    },
    [orderedItems]
  )

  const handleSelectAll = useCallback(() => {
    const currentFileIds = filteredFiles.map(f => f.document.id)
    const currentFolderPaths = filteredFolders.map(f => f.path)
    const allSelected =
      currentFileIds.every(id => selectedIds.has(id)) &&
      currentFolderPaths.every(p => selectedFolderPaths.has(p))

    if (allSelected) {
      setSelectedIds(new Set())
      setSelectedFolderPaths(new Set())
    } else {
      setSelectedIds(new Set(currentFileIds))
      setSelectedFolderPaths(new Set(currentFolderPaths))
    }
  }, [filteredFiles, filteredFolders, selectedIds, selectedFolderPaths])

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set())
    setSelectedFolderPaths(new Set())
  }, [])

  const handlePipelineSelect = useCallback((id: string) => {
    setSelectedPipelineId(id)
  }, [])

  const handleRetryPipeline = useCallback(() => {
    if (schema.status !== 'ready') return
    if (selectedIds.size === 0 && selectedFolderPaths.size === 0) return
    if (!selectedPipelineId) return
    setShowExtractionModal(true)
  }, [schema.status, selectedIds, selectedFolderPaths, selectedPipelineId])

  // Run extraction on every pending (or failed, on retry) doc in the case.
  // Backend's folder LIKE filter doesn't match empty paths, so we fetch the
  // doc IDs by status first and pass them explicitly.
  const [autoRunning, setAutoRunning] = useState(false)
  const videoSoundQuestion = useVideoSoundQuestion()
  const runExtractionOnPending = useCallback(
    async (statuses: string[] = ['uploaded']) => {
      if (autoRunning || schema.status !== 'ready') return
      setAutoRunning(true)
      try {
        // Endpoint hard-caps limit at 200; page through if there are more.
        const PAGE = 200
        const documentIds: string[] = []
        let holdsVideo = false
        for (let offset = 0; ; offset += PAGE) {
          const batch = await api.cases.getCaseDocuments(caseId, {
            status: statuses,
            limit: PAGE,
            offset,
          })
          // A capture is never re-extracted: "Retry failed" leaves failed captures alone.
          const extractable = new Set(extractableIds(batch, videoAvailable))
          documentIds.push(...extractable)
          holdsVideo ||= batch.some(
            doc => extractable.has(doc.id) && isVideoFileType(doc.file_type)
          )
          if (batch.length < PAGE) break
        }
        if (documentIds.length === 0) return
        // A batch that holds a video asks once whether its sound is processed too (PRD-0115); a video
        // the deployment cannot analyse is not in it (`extractableIds`).
        const videoSound = holdsVideo ? await videoSoundQuestion.ask() : false
        if (videoSound === null) return
        await api.cases.extractDocuments(caseId, {
          document_ids: documentIds,
          pipeline: AUTO_PIPELINE_ID,
          video_sound: videoSound,
        })
        void fetchTree(currentPath.length > 0 ? currentPath.join('/') : undefined)
        void queryClient.invalidateQueries({ queryKey: caseKeys.extractionRuns(caseId) })
      } catch (err: unknown) {
        console.error('Auto extraction failed:', err)
        // A source document of a HUMINT subject with captures is never re-extracted: say so.
        const refusal = captureRefusal(err, t)
        if (refusal) toast.error(refusal)
      } finally {
        setAutoRunning(false)
      }
    },
    [
      autoRunning,
      schema.status,
      caseId,
      currentPath,
      fetchTree,
      queryClient,
      t,
      videoAvailable,
      videoSoundQuestion.ask,
    ]
  )

  const handleRunExtraction = useCallback(
    async (
      schemaName?: string,
      userPrompt?: string,
      schemaVersion?: number,
      options?: RunExtractionOptions
    ) => {
      if (!selectedPipelineId || schema.status !== 'ready') return
      const excluded = new Set(options?.excludeDocumentIds ?? [])
      const documentIds = Array.from(selectedIds).filter(id => !excluded.has(id))
      if (documentIds.length === 0 && selectedFolderPaths.size === 0) {
        toast.info('Nothing to run — every selected file is handled by structured ingest')
        setShowExtractionModal(false)
        return
      }
      setIsRetrying(true)
      setExtractResults(null)
      // Auto sentinel passes through verbatim; explicit pipelines look up the template.
      const pipelineToSend =
        selectedPipelineId === AUTO_PIPELINE_ID
          ? AUTO_PIPELINE_ID
          : (pipelines.find(p => p.id === selectedPipelineId)?.template ?? selectedPipelineId)
      try {
        const response = await api.cases.extractDocuments(caseId, {
          document_ids: documentIds,
          folder_paths: Array.from(selectedFolderPaths),
          pipeline: pipelineToSend,
          schema_name: schemaName,
          schema_version: schemaVersion,
          user_prompt: userPrompt,
          video_sound: options?.videoSound === true,
        })
        setExtractResults(toExtractResults(response, excluded.size))
        setSelectedIds(new Set())
        setSelectedFolderPaths(new Set())
        void fetchTree(currentPath.length > 0 ? currentPath.join('/') : undefined)
        void queryClient.invalidateQueries({ queryKey: caseKeys.extractionRuns(caseId) })
      } catch (err: unknown) {
        console.error('Extraction failed:', err)
        toast.error(
          captureRefusal(err, t) ??
            videoAnalysisRefusal(err, t) ??
            errorMessage(err, 'Could not start extraction')
        )
      } finally {
        setIsRetrying(false)
        setShowExtractionModal(false)
      }
    },
    [
      caseId,
      selectedIds,
      selectedFolderPaths,
      fetchTree,
      currentPath,
      pipelines,
      selectedPipelineId,
      schema.status,
      queryClient,
      t,
    ]
  )

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirm | null>(null)

  const executeDelete = useCallback(
    async (body: { document_ids?: string[]; folder_paths?: string[] }) => {
      try {
        const result = await api.cases.bulkDeleteDocuments(caseId, body)
        setSelectedIds(new Set())
        setSelectedFolderPaths(new Set())
        void fetchTree(currentPath.length > 0 ? currentPath.join('/') : undefined)
        if (result.deleted > 0) {
          toast.success(`Deleted ${result.deleted} document${result.deleted === 1 ? '' : 's'}`)
        } else if (result.failed > 0) {
          toast.error(`Failed to delete ${result.failed} document${result.failed === 1 ? '' : 's'}`)
        } else {
          toast.warning('Nothing was deleted — selection no longer matches any documents')
        }
      } catch (err: unknown) {
        console.error('Delete failed:', err)
        // The API refuses any request touching a HUMINT capture (409 `capture_immutable`); say why.
        toast.error(captureRefusal(err, t) ?? inFlightRefusal(err, t) ?? 'Delete request failed')
      }
    },
    [caseId, fetchTree, currentPath, t]
  )

  const handleDeleteDocument = useCallback(
    (documentId: string) => {
      setDeleteConfirm({
        message: 'This will permanently delete the document and all its extracted entities.',
        onConfirm: () => executeDelete({ document_ids: [documentId] }),
      })
    },
    [executeDelete]
  )

  const handleDeleteFolder = useCallback(
    (folderPath: string, fileCount: number) => {
      setDeleteConfirm({
        message: `This will permanently delete all ${fileCount} documents in this folder and their extracted entities.`,
        onConfirm: () => executeDelete({ folder_paths: [folderPath] }),
      })
    },
    [executeDelete]
  )

  const handleBulkDelete = useCallback(() => {
    const docIds = Array.from(selectedIds)
    const folderPaths = Array.from(selectedFolderPaths)
    const total = docIds.length + folderPaths.length
    if (total === 0) return
    setDeleteConfirm({
      message: `This will permanently delete ${total} selected ${total === 1 ? 'item' : 'items'} and all extracted entities.`,
      onConfirm: () =>
        executeDelete({
          document_ids: docIds.length > 0 ? docIds : undefined,
          folder_paths: folderPaths.length > 0 ? folderPaths : undefined,
        }),
    })
  }, [selectedIds, selectedFolderPaths, executeDelete])

  const handleResetExtraction = useCallback(
    async (documentIds: string[]) => {
      if (documentIds.length === 0) return
      try {
        await api.cases.resetExtraction(caseId, documentIds)
        setSelectedIds(new Set())
        setSelectedFolderPaths(new Set())
        void fetchTree(currentPath.length > 0 ? currentPath.join('/') : undefined)
      } catch (err: unknown) {
        console.error('Reset extraction failed:', err)
        const refusal = captureRefusal(err, t)
        if (refusal) toast.error(refusal)
      }
    },
    [caseId, fetchTree, currentPath, t]
  )

  const handleResetSingleExtraction = useCallback(
    (docId: string) => {
      void handleResetExtraction([docId])
    },
    [handleResetExtraction]
  )

  // "Analyse video" queues the recording on the video lane; the row then shows the job's
  // state the way it shows any extraction's (scheduled → processing → processed / failed).
  // It asks first whether the sound is processed too (PRD-0115); a re-run replaces everything
  // the last run wrote (ADR-0089), so for a video analysed before the warning stands beside it.
  const startVideoAnalysis = useCallback(
    (docId: string, videoSound: boolean) => {
      void (async () => {
        try {
          await api.cases.startVideoAnalysis(caseId, docId, videoSound)
          toast.success(t('documents.video.queued'))
          void fetchTree(currentPath.length > 0 ? currentPath.join('/') : undefined)
        } catch (err: unknown) {
          console.error('Video analysis failed to start:', err)
          toast.error(videoAnalysisRefusal(err, t) ?? t('documents.video.failed'))
        }
      })()
    },
    [caseId, fetchTree, currentPath, t]
  )
  const handleAnalyseVideo = useCallback(
    (docId: string) => {
      const rerun = filteredFiles.find(f => f.document.id === docId)?.document.status !== 'uploaded'
      void videoSoundQuestion.ask({ rerun }).then(videoSound => {
        if (videoSound !== null) startVideoAnalysis(docId, videoSound)
      })
    },
    [filteredFiles, startVideoAnalysis, videoSoundQuestion.ask]
  )

  const selectionVideoSound = useMemo(
    () =>
      videoSoundScope(
        selectedDocumentMetas.map(doc => doc.file_type),
        selectedFolderPaths.size,
        videoAvailable
      ),
    [selectedDocumentMetas, selectedFolderPaths, videoAvailable]
  )

  const currentFileIds = useMemo(() => filteredFiles.map(f => f.document.id), [filteredFiles])
  const currentFolderPathsList = useMemo(() => filteredFolders.map(f => f.path), [filteredFolders])

  const selectAllChecked =
    totalItems > 0 &&
    currentFileIds.every(id => selectedIds.has(id)) &&
    currentFolderPathsList.every(p => selectedFolderPaths.has(p))

  const selectAllIndeterminate =
    !selectAllChecked &&
    (currentFileIds.some(id => selectedIds.has(id)) ||
      currentFolderPathsList.some(p => selectedFolderPaths.has(p)))

  // Selected capture files, and the captures inside selected folders (the `humint` folder).
  const selectedCaptureCount = useMemo(
    () =>
      countSelectedCaptures(
        (treeData?.files ?? []).map(f => f.document),
        selectedIds
      ) +
      (treeData?.folders ?? [])
        .filter(folder => selectedFolderPaths.has(folder.path))
        .reduce((sum, folder) => sum + (folder.capture_count ?? 0), 0),
    [treeData, selectedIds, selectedFolderPaths]
  )

  const selectedTotalItems = useMemo(() => {
    let count = selectedIds.size
    selectedFolderPaths.forEach(path => {
      const folder = filteredFolders.find(f => f.path === path)
      if (folder) count += folder.file_count
    })
    return count
  }, [selectedIds, selectedFolderPaths, filteredFolders])

  const breadcrumbItems = ['Home', ...currentPath]

  const pendingCount = statusCounts.uploaded ?? 0
  const failedCount = statusCounts.failed ?? 0

  // Pull the extraction runs so the "extracting" card reflects the runs' own
  // totals and progress rather than guessing from case-wide status counts.
  // Every uploaded audio file is a run of its own, so the card follows every
  // run that is still going — never only the newest one — and the runs are
  // read again for as long as any of them is: any that is not older than a
  // day, so a run that is stuck does not keep the page asking for ever.
  const runsQuery = useExtractionRuns(caseId, RUNS_POLL_MS)
  const runInProgress = useMemo(() => extractionInProgress(runsQuery.data), [runsQuery.data])

  return {
    schema,
    scrollRef,
    loading,
    loadingMore,
    treeData,
    orderedItems,
    filteredFolders,
    filteredFiles,
    displayedItems,
    totalItems,
    statusCounts,
    activeStatus,
    sort,
    toggleSort,
    searchQuery,
    setSearchQuery,
    statusFilters,
    clearStatusFilter: useCallback(() => setStatusFilters(new Set()), []),
    toggleStatusFilter: useCallback((value: string) => {
      setStatusFilters(prev => {
        const next = new Set<string>()
        if (!prev.has(value)) next.add(value)
        return next
      })
    }, []),
    currentPath,
    breadcrumbItems,
    navigateInto,
    navigateTo,
    selectedIds,
    selectedFolderPaths,
    hasSelection,
    selectAllChecked,
    selectAllIndeterminate,
    selectedTotalItems,
    selectedCaptureCount,
    handleItemToggle,
    handleSelectAll,
    handleClearSelection,
    pipelines,
    selectedPipelineId,
    handlePipelineSelect,
    selectedDocumentMetas,
    isRetrying,
    autoRunning,
    extractResults,
    showExtractionModal,
    setShowExtractionModal,
    handleRetryPipeline,
    handleRunExtraction,
    runExtractionOnPending,
    runInProgress,
    pendingCount,
    failedCount,
    deleteConfirm,
    setDeleteConfirm,
    handleDeleteDocument,
    handleDeleteFolder,
    handleBulkDelete,
    handleResetExtraction,
    handleResetSingleExtraction,
    videoAvailable,
    handleAnalyseVideo,
    selectionVideoSound,
    videoSoundQuestion,
  }
}
